import { forwardRef, useEffect, useImperativeHandle, useRef } from "react";
import * as THREE from "three";

const WORKS = [
  "/assets/work/work-1.jpg",
  "/assets/work/work-2.jpg",
  "/assets/work/work-3.jpg",
  "/assets/work/work-4.jpg",
  "/assets/work/work-5.jpg",
];
const N = WORKS.length;

// World-unit sizes scaled up ~45% from the previous pass (4.6 → 6.67,
// 3.1 → 4.5). The camera moves in a bit (13 → 15) so the bigger cards
// also read bigger on screen — not just a uniform world scale.
const CYL_RADIUS = 6.67;
const CARD_HEIGHT = 4.5;
const CARD_ANG_EXTENT = (60 * Math.PI) / 180;
const CARD_ARC_LENGTH = CARD_ANG_EXTENT * CYL_RADIUS;

// Evenly distribute cards around the cylinder when wrapped (72° apart for
// 5 cards). Cards span 60° each, leaving 12° gaps in the settled state.
const CARD_SPACING = (2 * Math.PI * CYL_RADIUS) / N;

// Tessellation along the strip direction. 48 segments is enough for the
// flat-to-curved morph to read silky-smooth while the per-frame buffer
// update stays well under a millisecond.
const SEGMENTS_X = 48;
const SEGMENTS_Y = 1;

const SPIN_PERIOD_MS = 42_000;

// Spin-handoff factor that blends a tiny linear component into the
// entry's easeOutCubic, so the entry's terminal velocity at t=1
// equals the live spin's constant velocity. Without this match, the
// entry decelerates to 0 right when the spin would need to ramp up
// from 0 — a 1+ second window where the cards are nearly still.
//
// Derivation: position s_entry(time) = f(t) · ENTRY_STRIP_DELTA with
// t = time / ENTRY_DURATION_MS. With f(t) = (1-K)·easeOutCubic(t) +
// K·t, f'(1) = K. Matching terminal velocity:
//   K · ENTRY_STRIP_DELTA / ENTRY_DURATION_MS =
//     2π · CYL_RADIUS / SPIN_PERIOD_MS
// For the current geometry (ENTRY_STRIP_DELTA ≈ 57, CYL_RADIUS = 6.67,
// SPIN_PERIOD_MS = 42000) and a 2500ms entry duration: K ≈ 0.044.
// If any of those change, recompute.
const SPIN_HANDOFF_K = 0.044;

// Where the leading card's center sits at t=0, on the +X tangent line off
// the right edge of the stage. Sized so the full queue is off-screen for
// the chosen camera/FOV across the container's range of aspect ratios.
const ENTRY_LEADING_OFFSET = 20;

// Total strip displacement during entry. The +CARD_ARC_LENGTH/2 ensures
// the trailing card's trailing vertex has crossed the wrap point by
// entry-end — so every card finishes the entry fully wrapped, no half-on
// half-off cards still mid-bend.
const ENTRY_STRIP_DELTA =
  ENTRY_LEADING_OFFSET + (N - 1) * CARD_SPACING + CARD_ARC_LENGTH / 2;

// Baked-in corner radius on each card's texture, as a fraction of the
// source image's width.
const CORNER_RADIUS_FRACTION = 0.014;

export type CylinderCarouselHandle = {
  // The carousel is fully scrubbable by external progress 0→1. Forward
  // entry, settled-spin, and reverse-unfurl all derive from the same
  // single scalar. No internal clocks, no .play()/.reverse() imperatives:
  // the master timeline in TimesAreChanging owns pacing for both
  // directions and pipes progressProxy.t into this method each frame.
  setEntryProgress: (progress: number) => void;
};

interface Props {
  onEntryComplete?: () => void;
}

function easeOutCubic(t: number): number {
  return 1 - Math.pow(1 - t, 3);
}

function clamp01(t: number): number {
  if (t < 0) return 0;
  if (t > 1) return 1;
  return t;
}

function buildCardTexture(
  image: HTMLImageElement,
  opts: { blur?: number; brightness?: number; saturate?: number; mirror?: boolean },
): THREE.CanvasTexture {
  const canvas = document.createElement("canvas");
  canvas.width = image.width;
  canvas.height = image.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("canvas 2d unavailable");

  const filterParts: string[] = [];
  if (opts.blur) filterParts.push(`blur(${opts.blur}px)`);
  if (opts.brightness !== undefined) filterParts.push(`brightness(${opts.brightness})`);
  if (opts.saturate !== undefined) filterParts.push(`saturate(${opts.saturate})`);
  if (filterParts.length) ctx.filter = filterParts.join(" ");

  if (opts.mirror) {
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
  }

  const r = canvas.width * CORNER_RADIUS_FRACTION;
  const w = canvas.width;
  const h = canvas.height;
  ctx.beginPath();
  if (typeof (ctx as CanvasRenderingContext2D & { roundRect?: unknown }).roundRect === "function") {
    (ctx as CanvasRenderingContext2D & {
      roundRect: (x: number, y: number, w: number, h: number, r: number) => void;
    }).roundRect(0, 0, w, h, r);
  } else {
    ctx.moveTo(r, 0);
    ctx.lineTo(w - r, 0);
    ctx.arcTo(w, 0, w, r, r);
    ctx.lineTo(w, h - r);
    ctx.arcTo(w, h, w - r, h, r);
    ctx.lineTo(r, h);
    ctx.arcTo(0, h, 0, h - r, r);
    ctx.lineTo(0, r);
    ctx.arcTo(0, 0, r, 0, r);
    ctx.closePath();
  }
  ctx.clip();

  ctx.drawImage(image, 0, 0);

  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.minFilter = THREE.LinearFilter;
  tex.magFilter = THREE.LinearFilter;
  tex.needsUpdate = true;
  return tex;
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

// Map a strip-position σ to a world point on the wrap path.
//   σ < 0  → on the +X tangent queue at (-σ, _, R), moving leftward as σ→0.
//   σ ≥ 0  → on the cylinder at angle φ=σ/R, world (-R sin φ, _, R cos φ).
// The two branches share the same value AND first derivative at σ=0, so a
// vertex crossing the wrap point traces a smooth C¹ arc — no visible
// kink between the flat queue and the curved cylinder.
function placeOnPath(sigma: number, radius: number, out: { x: number; z: number }): void {
  if (sigma < 0) {
    out.x = -sigma;
    out.z = radius;
  } else {
    const phi = sigma / radius;
    out.x = -radius * Math.sin(phi);
    out.z = radius * Math.cos(phi);
  }
}

function buildCardGeometry(): THREE.PlaneGeometry {
  // PlaneGeometry gives us correct UVs out of the box. The vertex positions
  // are placeholders; updateCardGeometry rewrites the position attribute
  // each frame to follow the wrap path.
  return new THREE.PlaneGeometry(CARD_ARC_LENGTH, CARD_HEIGHT, SEGMENTS_X, SEGMENTS_Y);
}

// Redraw a card's vertices from its strip-center σ. Each vertex is placed
// independently along the wrap path, so a card straddling σ=0 looks
// half-flat / half-curved — that's the visible "swoop" of the leading
// edge curling onto the cylinder while the trailing edge is still flat.
const _tmpPath = { x: 0, z: 0 };
function updateCardGeometry(geom: THREE.BufferGeometry, sCenter: number): void {
  const posAttr = geom.attributes.position as THREE.BufferAttribute;
  const arr = posAttr.array as Float32Array;
  const cols = SEGMENTS_X + 1;
  const rows = SEGMENTS_Y + 1;
  for (let r = 0; r < rows; r++) {
    const y = (0.5 - r) * CARD_HEIGHT;
    for (let c = 0; c < cols; c++) {
      const xOrig = (c / SEGMENTS_X - 0.5) * CARD_ARC_LENGTH;
      // σ_vertex = σ_center − xOrig: leading edge (xOrig < 0) gets a
      // larger σ, so it wraps first. This also keeps the texture reading
      // L→R correctly: u=0 at xOrig=-arc/2 lands on screen-left both in
      // the queue and on the cylinder.
      const sigma = sCenter - xOrig;
      placeOnPath(sigma, CYL_RADIUS, _tmpPath);
      const idx = (r * cols + c) * 3;
      arr[idx] = _tmpPath.x;
      arr[idx + 1] = y;
      arr[idx + 2] = _tmpPath.z;
    }
  }
  posAttr.needsUpdate = true;
}

export const CylinderCarousel = forwardRef<CylinderCarouselHandle, Props>(function CylinderCarousel(
  { onEntryComplete },
  ref,
) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const entryProgRef = useRef<number>(0);
  const entryCompleteFiredRef = useRef(false);
  // Resting spin lives outside of `entryProg` because it loops forever
  // and can't be a child of a scrubbable timeline. State machine:
  //   • t < 1, no prior spin                → no spin contribution
  //   • t hits 1 for the first time         → spinStartAt = now()
  //   • t stays at 1                        → live spin accumulates
  //   • t drops below 1                     → freeze spin into frozen;
  //                                          add frozen × t to sBase so
  //                                          spin bleeds out to 0 by t=0
  //                                          while cards uncurl
  //   • t reaches 0                         → reset both refs
  //   • t returns to 1 from un-furl         → spinStartAt resets so live
  //                                          spin starts fresh from 0
  // Position AND velocity are continuous at the t=1→0.9999 boundary
  // because the frozen value captured one frame after the drop equals
  // the live spin one frame before it.
  const spinStartAtRef = useRef<number | null>(null);
  const spinArcFrozenRef = useRef<number | null>(null);
  const onEntryCompleteRef = useRef(onEntryComplete);
  onEntryCompleteRef.current = onEntryComplete;

  useImperativeHandle(
    ref,
    () => ({
      setEntryProgress(progress: number) {
        entryProgRef.current = clamp01(progress);
      },
    }),
    [],
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    let width = container.clientWidth || 800;
    let height = container.clientHeight || 360;

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    container.appendChild(renderer.domElement);

    // iOS Safari (and other tight-memory environments) can drop the WebGL
    // context under pressure. Without a handler the canvas freezes silently
    // AND any parent timeline scrubbing this carousel hangs forever waiting
    // for the entry-complete callback. Pause the loop, fire the completion
    // signal once so the timeline unblocks, and leave the last-drawn frame
    // in place (recreating textures/materials/geometry after restore would
    // be a substantially larger change).
    let raf = 0;
    let contextLost = false;
    const handleContextLost = (e: Event) => {
      e.preventDefault();
      contextLost = true;
      if (raf) {
        cancelAnimationFrame(raf);
        raf = 0;
      }
      if (!entryCompleteFiredRef.current) {
        entryCompleteFiredRef.current = true;
        onEntryCompleteRef.current?.();
      }
      console.warn("CylinderCarousel: WebGL context lost — animation paused");
    };
    const handleContextRestored = () => {
      console.warn(
        "CylinderCarousel: WebGL context restored — reload the page to reinitialize",
      );
    };
    renderer.domElement.addEventListener("webglcontextlost", handleContextLost, false);
    renderer.domElement.addEventListener("webglcontextrestored", handleContextRestored, false);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(32, width / height, 0.1, 100);
    // Pull the camera back as the container narrows so the leading card
    // never gets clipped against the stage edges. Piecewise:
    //   • w ≥ ref (1100): no pullback (desktop).
    //   • hinge ≤ w < ref: gentle pow-0.30 falloff. At the 768-width
    //     stage (~472px) cards render ~18% larger than under sqrt,
    //     filling the col-2-5 stage instead of looking starved inside.
    //   • w < hinge: steeper pow-0.70 falloff so the leading/trailing
    //     cards' rotation arc fits inside the canvas at 320/425 widths
    //     (single-exponent curves either starve 768 or clip ≤425).
    //   • The branches meet continuously at hinge = 380.
    const computeCameraZ = (w: number): number => {
      const ref = 1100;
      const minZ = 15;
      const hinge = 380;
      if (w >= ref) return minZ;
      if (w >= hinge) return minZ * Math.pow(ref / w, 0.30);
      const zAtHinge = minZ * Math.pow(ref / hinge, 0.30);
      return zAtHinge * Math.pow(hinge / w, 0.70);
    };
    camera.position.set(0, 0, computeCameraZ(width));
    camera.lookAt(0, 0, 0);

    type Card = {
      group: THREE.Group;
      geom: THREE.BufferGeometry;
      stripOffset: number;
    };
    const cards: Card[] = [];
    const disposables: Array<{ dispose: () => void }> = [];

    Promise.all(WORKS.map(loadImage))
      .then((images) => {
        images.forEach((img, i) => {
          const frontTex = buildCardTexture(img, {});
          const backTex = buildCardTexture(img, {
            blur: 28,
            brightness: 0.32,
            saturate: 1.5,
            mirror: true,
          });

          // Single geometry shared between front and back meshes: same
          // vertex buffer, different materials. Updating once per frame
          // moves both sides.
          const geom = buildCardGeometry();

          const frontMat = new THREE.MeshBasicMaterial({
            map: frontTex,
            side: THREE.FrontSide,
            transparent: true,
          });
          const backMat = new THREE.MeshBasicMaterial({
            map: backTex,
            side: THREE.BackSide,
            transparent: true,
          });

          const frontMesh = new THREE.Mesh(geom, frontMat);
          const backMesh = new THREE.Mesh(geom, backMat);
          // Vertices change every frame; let the renderer skip the bounding
          // sphere recompute and just always draw the cards.
          frontMesh.frustumCulled = false;
          backMesh.frustumCulled = false;

          const group = new THREE.Group();
          group.add(frontMesh);
          group.add(backMesh);

          // Park each card on its queue position before the first render
          // so there's no flash at frame 0.
          const initialS = -ENTRY_LEADING_OFFSET - i * CARD_SPACING;
          updateCardGeometry(geom, initialS);

          scene.add(group);
          cards.push({ group, geom, stripOffset: i * CARD_SPACING });
          disposables.push(geom, frontMat, backMat, frontTex, backTex);
        });
        renderer.render(scene, camera);
      })
      .catch((err) => {
        console.error("CylinderCarousel: failed to load textures", err);
      });

    if (reduced) {
      return () => {
        renderer.domElement.removeEventListener("webglcontextlost", handleContextLost);
        renderer.domElement.removeEventListener("webglcontextrestored", handleContextRestored);
        renderer.dispose();
        for (const d of disposables) d.dispose();
        if (renderer.domElement.parentNode === container) {
          container.removeChild(renderer.domElement);
        }
      };
    }

    const computeLiveSpinArc = (now: number): number => {
      const start = spinStartAtRef.current;
      if (start == null) return 0;
      const postEntryMs = now - start;
      // Constant-velocity spin. The entry easing already lands at the
      // spin velocity (via SPIN_HANDOFF_K), so no ramp-up is needed —
      // adding one would create a velocity dip right after t=1.
      return (postEntryMs / SPIN_PERIOD_MS) * 2 * Math.PI * CYL_RADIUS;
    };

    const animate = () => {
      if (contextLost) return;
      const t = entryProgRef.current;
      // Blended easing: easeOutCubic gives the wrap-onto-cylinder
      // phase its dramatic deceleration, while a small linear term
      // (SPIN_HANDOFF_K) sets the terminal velocity at t=1 equal to
      // the live spin's velocity. Result: cards rush in, decelerate
      // smoothly through the wrap, and continue at constant spin
      // velocity past t=1 with no perceptible inflection.
      const entryEased =
        (1 - SPIN_HANDOFF_K) * easeOutCubic(t) + SPIN_HANDOFF_K * t;
      let sBase = -ENTRY_LEADING_OFFSET + entryEased * ENTRY_STRIP_DELTA;
      const now = performance.now();

      if (t >= 1) {
        // Settled: live spin from a fresh clock. spinArcFrozen guard so
        // a 4→3 return (partial reverse then re-forward) restarts spin
        // from 0 rather than continuing the wallclock the freeze paused.
        if (spinStartAtRef.current == null || spinArcFrozenRef.current != null) {
          spinStartAtRef.current = now;
        }
        spinArcFrozenRef.current = null;
        sBase += computeLiveSpinArc(now);
        if (!entryCompleteFiredRef.current) {
          entryCompleteFiredRef.current = true;
          onEntryCompleteRef.current?.();
        }
      } else if (t > 0) {
        // Forward entry OR reverse un-furl. If we just dropped from
        // settled, capture the live spin once and scale it with t.
        if (spinStartAtRef.current != null && spinArcFrozenRef.current == null) {
          spinArcFrozenRef.current = computeLiveSpinArc(now);
        }
        if (spinArcFrozenRef.current != null) {
          sBase += spinArcFrozenRef.current * t;
        }
        entryCompleteFiredRef.current = false;
      } else {
        // Fully un-furled or pre-cinematic. Reset spin clocks so the
        // next forward pass spins up from zero.
        spinStartAtRef.current = null;
        spinArcFrozenRef.current = null;
        entryCompleteFiredRef.current = false;
      }

      for (const card of cards) {
        updateCardGeometry(card.geom, sBase - card.stripOffset);
      }

      renderer.render(scene, camera);
      raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);

    const resize = () => {
      width = container.clientWidth || width;
      height = container.clientHeight || height;
      camera.aspect = width / height;
      camera.position.z = computeCameraZ(width);
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
    };
    const observer = new ResizeObserver(resize);
    observer.observe(container);

    return () => {
      if (raf) cancelAnimationFrame(raf);
      observer.disconnect();
      renderer.domElement.removeEventListener("webglcontextlost", handleContextLost);
      renderer.domElement.removeEventListener("webglcontextrestored", handleContextRestored);
      renderer.dispose();
      for (const d of disposables) d.dispose();
      if (renderer.domElement.parentNode === container) {
        container.removeChild(renderer.domElement);
      }
    };
  }, []);

  return <div ref={containerRef} className="cyl-stage" aria-hidden="true" />;
});
